// Thème Maison : ventes croisées.
// - Bouton « Ajouter » (en ligne, colonnes, carrousel) : ajout direct au panier.
// - Case à cocher : les produits cochés partent juste après l'ajout réussi du bouton d'achat
//   principal de la même section (on écoute l'événement panier émis par product-form-component).
// - Automatique : demande la section à la route des recommandations « complémentaires ».
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { addItems, getCart, formatMoney } from '@theme/mx-cart';

const ADDED_DURATION = 2500;
const ERROR_DURATION = 8000;

/**
 * @typedef {object} CrossSellRefs
 * @property {HTMLUListElement} list
 * @property {HTMLButtonElement} [prev]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLElement} [dots]
 * @property {HTMLElement} [status]
 * @property {HTMLElement} [blockError]
 * @property {HTMLElement} [editorNote]
 * @extends {Component<CrossSellRefs>}
 */
class MxCrossSell extends Component {
  requiredRefs = ['list'];

  /** @type {AbortController | undefined} */
  #abort;
  /** @type {number | undefined} */
  #scrollFrame;
  /** @type {Set<string>} produits ajoutés par ce bloc, gardés visibles le temps du message */
  #recentlyAdded = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });

    if (this.dataset.layout === 'bump') {
      const scope = this.closest('.shopify-section') ?? document;
      scope.addEventListener(StandardEvents.cartLinesUpdate, this.#onMainAdd, { signal });
    }

    if (this.dataset.hideInCart === 'true') {
      document.addEventListener(StandardEvents.cartLinesUpdate, this.#onAnyCartUpdate, { signal });
    }

    if (this.dataset.layout === 'carousel') {
      this.refs.list.addEventListener('scroll', this.#onScroll, { signal, passive: true });
      window.addEventListener('resize', this.#onScroll, { signal, passive: true });
    }

    if (this.dataset.autoUrl) {
      this.#loadRecommendations();
    } else {
      this.#refreshState();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    if (this.#scrollFrame) cancelAnimationFrame(this.#scrollFrame);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#refreshState();
  }

  /* ---------- Affichage ---------- */

  get #items() {
    return /** @type {HTMLLIElement[]} */ ([...this.refs.list.querySelectorAll(':scope > .mx-cross-sell__item')]);
  }

  #refreshState() {
    const visible = this.#items.filter((item) => !item.hidden);
    if (visible.length === 0 && !this.refs.editorNote) {
      this.setAttribute('data-empty', '');
    } else {
      this.removeAttribute('data-empty');
    }
    if (this.refs.editorNote) this.refs.editorNote.hidden = visible.length > 0;

    if (this.dataset.layout === 'carousel') {
      this.#buildDots();
      this.#updateCarousel();
    }
  }

  /** @param {string} message */
  #announce(message) {
    const { status } = this.refs;
    if (!status) return;
    status.textContent = '';
    requestAnimationFrame(() => {
      status.textContent = message;
    });
  }

  /* ---------- Recommandations automatiques ---------- */

  async #loadRecommendations() {
    const url = this.dataset.autoUrl;
    if (!url) return;
    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.getElementById(this.refs.list.id);
      if (fresh) this.refs.list.innerHTML = fresh.innerHTML;
    } catch (error) {
      console.warn('[mx-cross-sell] Recommandations indisponibles', error);
    }
    this.#refreshState();
  }

  /* ---------- Variantes ---------- */

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.matches('select[data-variant-picker], [data-variant-picker] input[type="radio"]')) return;

    const item = target.closest('.mx-cross-sell__item');
    if (!(item instanceof HTMLElement)) return;

    /** @type {HTMLElement | null} */
    let chosen = null;
    if (target instanceof HTMLSelectElement) {
      chosen = target.selectedOptions[0] ?? null;
    } else if (target instanceof HTMLInputElement && target.checked) {
      chosen = target;
    }
    if (!chosen) return;

    const value = chosen instanceof HTMLOptionElement || chosen instanceof HTMLInputElement ? chosen.value : '';
    item.dataset.variantId = value;
    item.dataset.price = chosen.dataset.price ?? item.dataset.price;
    item.dataset.compare = chosen.dataset.compare ?? '0';
    this.#clearError(item);
    this.#renderPrice(item);

    const imageUrl = chosen.dataset.image;
    const img = item.querySelector('img.mx-cross-sell__image');
    if (imageUrl && img instanceof HTMLImageElement) {
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = imageUrl;
    }
  };

  /** @param {HTMLElement} item */
  #renderPrice(item) {
    const price = Number(item.dataset.price) || 0;
    const compare = Number(item.dataset.compare) || 0;
    const formatted = formatMoney(price);

    item.querySelectorAll('[data-price-current], [data-price-token]').forEach((el) => {
      el.textContent = formatted;
    });

    const compareEl = item.querySelector('[data-price-compare]');
    const compareValue = item.querySelector('[data-price-compare-value]');
    // Le prix barré n'est affiché que si le réglage l'autorise (élément rendu) et s'il est vrai.
    if (compareEl instanceof HTMLElement && compareValue) {
      const show = compare > price && this.dataset.showCompare === 'true';
      compareEl.hidden = !show;
      if (show) compareValue.textContent = formatMoney(compare);
    }
  }

  /* ---------- Bouton « Ajouter » ---------- */

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const addButton = target.closest('button[data-add]');
    if (addButton instanceof HTMLButtonElement) {
      this.#addOne(addButton);
      return;
    }

    if (this.dataset.layout === 'carousel') {
      if (target.closest('[ref="prev"]')) this.#scrollByPage(-1);
      else if (target.closest('[ref="next"]')) this.#scrollByPage(1);
      else {
        const dot = target.closest('.mx-cross-sell__dot');
        if (dot instanceof HTMLElement) this.#scrollToIndex(Number(dot.dataset.index) || 0);
      }
    }
  };

  /** @param {HTMLButtonElement} button */
  async #addOne(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    const item = button.closest('.mx-cross-sell__item');
    if (!(item instanceof HTMLElement)) return;

    const variantId = item.dataset.variantId;
    if (!variantId) return;

    const textEl = button.querySelector('[data-add-text]');
    const originalText = textEl?.textContent ?? '';

    this.#clearError(item);
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    try {
      const productId = item.dataset.productId ?? '';
      this.#recentlyAdded.add(productId);
      await addItems([{ id: Number(variantId), quantity: 1 }], this, { context: 'product' });

      const addedLabel = this.dataset.addedLabel || originalText;
      if (textEl) textEl.textContent = addedLabel;
      button.removeAttribute('aria-busy');
      button.dataset.added = 'true';
      const name = item.querySelector('.mx-cross-sell__name')?.textContent?.trim() ?? '';
      this.#announce(`${name} : ${addedLabel}`);

      window.setTimeout(() => {
        if (textEl) textEl.textContent = originalText;
        delete button.dataset.added;
        button.disabled = false;
        this.#recentlyAdded.delete(productId);
        if (this.dataset.hideInCart === 'true') this.#syncWithCart();
      }, ADDED_DURATION);
    } catch (error) {
      this.#recentlyAdded.delete(item.dataset.productId ?? '');
      button.removeAttribute('aria-busy');
      button.disabled = false;
      this.#showError(item, error);
    }
  }

  /**
   * @param {HTMLElement} item
   * @param {unknown} error
   */
  #showError(item, error) {
    const box = item.querySelector('[data-error]');
    if (!(box instanceof HTMLElement)) return;
    box.textContent = this.#errorMessage(error);
    box.hidden = false;
    window.setTimeout(() => {
      box.hidden = true;
      box.textContent = '';
    }, ERROR_DURATION);
  }

  /** @param {HTMLElement} item */
  #clearError(item) {
    const box = item.querySelector('[data-error]');
    if (box instanceof HTMLElement) {
      box.hidden = true;
      box.textContent = '';
    }
  }

  /** @param {unknown} error */
  #errorMessage(error) {
    const fallback = this.dataset.errorText || '';
    // Seuls les messages renvoyés par Shopify (Error simple venant de mx-cart) sont montrés tels quels :
    // une panne réseau (TypeError) ou une réponse illisible (SyntaxError) donnent le message du réglage.
    if (error instanceof Error && error.constructor === Error && error.message && error.message !== 'Ajout impossible') {
      return error.message;
    }
    return fallback;
  }

  /* ---------- Case à cocher : ajout avec le bouton d'achat principal ---------- */

  /** @param {Event} event */
  #onMainAdd = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLElement) || form.localName !== 'product-form-component') return;

    const cartEvent = /** @type {any} */ (event);
    if (cartEvent.action && cartEvent.action !== 'add') return;

    const mainId = this.dataset.mainProductId;
    if (mainId && form.dataset.productId && form.dataset.productId !== mainId) return;

    // On lit les cases au moment du clic, avant la réponse du panier.
    const checked = this.#items
      .filter((item) => !item.hidden)
      .map((item) => item.querySelector('input.mx-cross-sell__check'))
      .filter((input) => input instanceof HTMLInputElement && input.checked);
    const items = checked
      .map((input) => input.closest('.mx-cross-sell__item'))
      .filter((item) => item instanceof HTMLElement && item.dataset.variantId)
      .map((item) => ({ id: Number(/** @type {HTMLElement} */ (item).dataset.variantId), quantity: 1 }));

    if (items.length === 0 || !cartEvent.promise) return;

    cartEvent.promise
      .then(async (/** @type {any} */ result) => {
        if (result?.detail?.didError) return;
        await this.#addBumped(items, checked);
      })
      .catch(() => {
        // L'ajout principal a échoué : on n'ajoute rien.
      });
  };

  /**
   * @param {{id: number, quantity: number}[]} items
   * @param {HTMLInputElement[]} checked
   */
  async #addBumped(items, checked) {
    const { blockError } = this.refs;
    if (blockError) {
      blockError.hidden = true;
      blockError.textContent = '';
    }

    try {
      const cart = await addItems(items, this, { context: 'product' });
      checked.forEach((input) => {
        input.checked = false;
      });
      this.#announce(this.dataset.bumpSuccess || '');
      if (this.dataset.hideInCart === 'true' && cart?.items) this.#applyCart(cart);
    } catch (error) {
      if (!blockError) return;
      blockError.textContent = this.#errorMessage(error);
      blockError.hidden = false;
      window.setTimeout(() => {
        blockError.hidden = true;
        blockError.textContent = '';
      }, ERROR_DURATION);
    }
  }

  /* ---------- Masquer ce qui est déjà dans le panier ---------- */

  /** @param {Event} event */
  #onAnyCartUpdate = (event) => {
    const cartEvent = /** @type {any} */ (event);
    // Nos propres ajouts : on garde la carte visible le temps du message « Ajouté ».
    if (event.target instanceof Node && this.contains(event.target)) return;
    cartEvent.promise
      ?.then(() => this.#syncWithCart())
      .catch(() => {});
  };

  async #syncWithCart() {
    try {
      const cart = await getCart();
      this.#applyCart(cart);
    } catch {
      // Panier illisible : on ne change rien.
    }
  }

  /** @param {{items: {product_id: number}[]}} cart */
  #applyCart(cart) {
    const inCart = new Set((cart.items ?? []).map((line) => String(line.product_id)));
    for (const item of this.#items) {
      const productId = item.dataset.productId ?? '';
      if (this.#recentlyAdded.has(productId)) continue;
      item.hidden = inCart.has(productId);
    }
    this.#refreshState();
  }

  /* ---------- Carrousel ---------- */

  #visibleItems() {
    return this.#items.filter((item) => !item.hidden);
  }

  #step() {
    const [first, second] = this.#visibleItems();
    if (!first) return this.refs.list.clientWidth;
    if (second) return Math.abs(second.offsetLeft - first.offsetLeft);
    return first.getBoundingClientRect().width;
  }

  /** @param {number} direction */
  #scrollByPage(direction) {
    const { list } = this.refs;
    const step = this.#step() || list.clientWidth;
    const perView = Math.max(1, Math.floor((list.clientWidth + 1) / step));
    list.scrollBy({ left: direction * step * perView });
  }

  /** @param {number} index */
  #scrollToIndex(index) {
    const target = this.#visibleItems()[index];
    if (!target) return;
    // La liste est positionnée (CSS) : offsetLeft se mesure depuis son bord.
    this.refs.list.scrollTo({ left: target.offsetLeft });
  }

  #onScroll = () => {
    if (this.#scrollFrame) return;
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = undefined;
      this.#updateCarousel();
    });
  };

  #buildDots() {
    const { dots } = this.refs;
    if (!dots) return;
    const count = this.#visibleItems().length;
    dots.replaceChildren();
    if (count < 2) return;
    for (let i = 0; i < count; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'mx-cross-sell__dot';
      dot.dataset.index = String(i);
      dot.setAttribute('aria-label', `Produit ${i + 1} sur ${count}`);
      dot.setAttribute('aria-controls', this.refs.list.id);
      dots.append(dot);
    }
  }

  #updateCarousel() {
    const { list, prev, next, dots } = this.refs;
    const maxScroll = list.scrollWidth - list.clientWidth;
    const position = Math.abs(list.scrollLeft);

    if (prev) prev.disabled = position <= 2;
    if (next) next.disabled = position >= maxScroll - 2;

    if (dots) {
      const step = this.#step() || 1;
      const count = dots.children.length;
      const active = position >= maxScroll - 2 ? count - 1 : Math.round(position / step);
      [...dots.children].forEach((dot, i) => {
        dot.setAttribute('aria-current', String(i === active));
      });
    }
  }
}

if (!customElements.get('mx-cross-sell')) {
  customElements.define('mx-cross-sell', MxCrossSell);
}
